import React, { Component } from 'react';
import { dateStringToDisplayDate } from '../Utilities/UtilityFunctions';
import { switchMap } from 'rxjs/operators';
import Fund from '../assets/img/OCSHeartSign.png';
import { Observable, Subscription } from 'rxjs';
import FundProxy from './FundProxy';

interface ThermometerProps {
  readyObservable: Observable<boolean>;
  fundDetailsProvider: FundProxy;
}

interface ThermometerState {
  goalAmount: number;
  goalAmountDisplay: string;
  asOfDateDisplay: string;
  goalYearDisplay: string;
  animationTime: number;
  numberPrefix: string;
  numberSuffix: string;
  displayAmount: number;
  widthOfNumbers: number;
  tickMarkSegementCount: number;
  tickHeight: number;
  numberStartY: number;
  thermTopHeight: number;
  thermBottomHeight: number;
  mercuryHeightOffset: number;
  tooltipOffset: number;
  heightOfBody: number;
  heightOfBottom: number;
  goalThermometerHeight: number;
  topOfThermBodyMercury: number;
  heightOfThermBodyMercury: number;
  topOfThermTooltip: number;
  glassTopImg: string;
  glassBodyImg: string;
  redVerticalImg: string;
  thermBodyForeImg: string;
  glassBottomImg: string;
  tootipPointImg: string;
  tooltipMiddleImg: string;
  tooltipButtImg: string;
}

class Thermometer extends Component<ThermometerProps, ThermometerState> {
  fundDetailsSubscription: Subscription;

  constructor(props: ThermometerProps) {
    super(props);
    const resolution2x = window.devicePixelRatio === 2;

    const defaultState = {
      goalAmount: 0,
      goalAmountDisplay: 'Loading...',
      asOfDateDisplay: 'Loading...',
      goalYearDisplay: '',
      animationTime: 3000,
      numberPrefix: '$',
      numberSuffix: '',
      displayAmount: 0,
      widthOfNumbers: 100,
      tickMarkSegementCount: 4,
      tickHeight: 40,
      numberStartY: 6,
      thermTopHeight: 13,
      thermBottomHeight: 51,
      mercuryHeightOffset: 10,
      tooltipOffset: 15,
      heightOfBody: 0,
      heightOfBottom: 0,
      goalThermometerHeight: 0,
      topOfThermBodyMercury: 0,
      heightOfThermBodyMercury: 0,
      topOfThermTooltip: 0,
      glassTopImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/glassTop${resolution2x ? '2x' : ''}.png`,
      glassBodyImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/glassBody${resolution2x ? '2x' : ''}.png`,
      redVerticalImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/redVertical${resolution2x ? '2x' : ''}.png`,
      thermBodyForeImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/tickShine${resolution2x ? '2x' : ''}.png`,
      glassBottomImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/glassBottom${resolution2x ? '2x' : ''}.png`,
      tootipPointImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/tooltipPoint${resolution2x ? '2x' : ''}.png`,
      tooltipMiddleImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/tooltipMiddle${resolution2x ? '2x' : ''}.png`,
      tooltipButtImg: `https://s3-us-west-2.amazonaws.com/s.cdpn.io/61826/tooltipButt${resolution2x ? '2x' : ''}.png`,
    };

    defaultState.heightOfBody = defaultState.tickMarkSegementCount * defaultState.tickHeight;
    defaultState.heightOfBottom = defaultState.heightOfBody + defaultState.thermTopHeight;
    defaultState.goalThermometerHeight = defaultState.heightOfBody + defaultState.thermTopHeight + defaultState.thermBottomHeight;
    defaultState.topOfThermTooltip = defaultState.heightOfBody + defaultState.thermTopHeight - defaultState.tooltipOffset;

    this.state = defaultState;

    this.animateThermometer = this.animateThermometer.bind(this);
  }

  componentDidMount() {
    const animateThermometer = this.animateThermometer;

    this.fundDetailsSubscription = this.props.readyObservable
      .pipe(switchMap(() => this.props.fundDetailsProvider.getDetails()))
      .subscribe({
        next: async (fundDetails) => {
          const goalAmountInput = fundDetails.goalAmount;
          const currentAmountInput = fundDetails.fundGroups?.find((x) => x.name == 'National Bahá’í Fund Total')?.amount;
          const asOfDate = fundDetails.statusDate;
          const goalYear = fundDetails.goalYear;
          const startTime = new Date().getTime();

          if (!goalAmountInput || !currentAmountInput || !asOfDate || !goalYear) {
            throw new Error('Fund deails are absent');
          }

          requestAnimationFrame(() => {
            animateThermometer(startTime, goalAmountInput, currentAmountInput, asOfDate, goalYear.toString());
          });
        },
        error: async (error) => console.log('Error getting fund details: ', error),
      });
  }

  componentWillUnmount() {
    this.fundDetailsSubscription.unsubscribe();
  }

  commaSeparateNumber(val: number) {
    let roundedVal: number | string = Math.round(val);
    while (/(\d+)(\d{3})/.test(roundedVal.toString())) {
      roundedVal = roundedVal.toString().replace(/(\d+)(\d{3})/, '$1,$2');
    }
    return this.state.numberPrefix + roundedVal + this.state.numberSuffix;
  }

  buildThermNumbers() {
    const thermNumbers = [];
    const countPerTick = this.state.goalAmount / this.state.tickMarkSegementCount;
    for (let i = 0; i < this.state.tickMarkSegementCount; i++) {
      const yPos = this.state.tickHeight * i + this.state.numberStartY;
      const style = {
        top: yPos + 'px',
        width: this.state.widthOfNumbers + 'px',
      };
      const dollarText = this.commaSeparateNumber(this.state.goalAmount - countPerTick * i);
      const uniqueKey = 'thermNumbers_' + i;
      thermNumbers.push(
        <div key={uniqueKey} className="therm-number" style={style}>
          {dollarText}
        </div>
      );
    }

    return thermNumbers;
  }

  animateThermometer(
    startTime: number,
    goalAmountInput: number,
    currentAmountInput: number,
    asOfDateInput: string,
    goalYearInput: string
  ) {
    const currentTime = new Date().getTime();
    const calculatedAnimationProgress = (currentTime - startTime) / this.state.animationTime;
    const animationProgress = calculatedAnimationProgress > 1 ? 1 : calculatedAnimationProgress;
    const percentageComplete = currentAmountInput / goalAmountInput;
    const distanceToRise = (this.state.heightOfBody * percentageComplete + 1) * animationProgress;
    const mercuryHeight = this.state.mercuryHeightOffset + distanceToRise;
    const newMercuryTop = this.state.heightOfBody + this.state.thermTopHeight - distanceToRise;
    const newTopOfThermTooltip = this.state.heightOfBody + this.state.thermTopHeight - this.state.tooltipOffset - distanceToRise;

    let newDisplayAmount = currentAmountInput * animationProgress;
    if (newDisplayAmount >= currentAmountInput) {
      newDisplayAmount = currentAmountInput;
    }

    let newState: Partial<ThermometerState> = {
      topOfThermBodyMercury: newMercuryTop,
      heightOfThermBodyMercury: mercuryHeight,
      topOfThermTooltip: newTopOfThermTooltip,
      displayAmount: newDisplayAmount,
    };

    if (this.state.goalAmount === 0) {
      const goalAmountToDisplay = Math.round(goalAmountInput / 1000000);
      Object.assign(newState, {
        goalAmount: goalAmountInput,
        goalAmountDisplay: '$' + goalAmountToDisplay + 'M',
        asOfDateDisplay: dateStringToDisplayDate(asOfDateInput),
        goalYearDisplay: goalYearInput,
      });
    }
    this.setState(newState as ThermometerState);

    if (newDisplayAmount < currentAmountInput) {
      requestAnimationFrame(() => {
        this.animateThermometer(startTime, goalAmountInput, currentAmountInput, asOfDateInput, goalYearInput);
      });
    }
  }

  render() {
    const styles = {
      goalThermometerContainer: {
        paddingBottom: '10px',
      },
      goalThermometer: {
        zIndex: 1,
        height: this.state.goalThermometerHeight,
        opacity: 1,
      },
      themGraphics: {
        left: this.state.widthOfNumbers,
      },
      thermBodyBg: {
        height: this.state.heightOfBody,
      },
      thermBodyMercury: {
        top: this.state.topOfThermBodyMercury,
        height: this.state.heightOfThermBodyMercury,
      },
      thermBodyFore: {
        height: this.state.heightOfBody,
        backgroundImage: 'url(' + this.state.thermBodyForeImg + ')',
      },
      thermBottom: {
        top: this.state.heightOfBottom,
      },
      thermTooltip: {
        top: this.state.topOfThermTooltip,
      },
      thermToolTipMiddle: {
        backgroundImage: 'url(' + this.state.tooltipMiddleImg + ')',
      },
      fundHeart: {
        height: '30px',
        margin: '5px 0px 10px 0px',
      },
    };

    return (
      <div className="center">
        <img src={Fund} style={styles.fundHeart} alt="Fund Heart Logo" />
        <h4>National Bahá’í Fund</h4>
        {this.state.goalYearDisplay ? (
          <h4>
            {this.state.goalYearDisplay} Fund Goal: {this.state.goalAmountDisplay}
          </h4>
        ) : null}
        <p>As of {this.state.asOfDateDisplay}</p>
        <div className="thermobit">
          <div id="goal-thermometer-container" style={styles.goalThermometerContainer}>
            <div id="goal-thermometer" style={styles.goalThermometer}>
              <div id="therm-numbers">{this.buildThermNumbers()}</div>
              <div id="therm-graphics" style={styles.themGraphics}>
                <img alt="thermometer top" id="therm-top" src={this.state.glassTopImg} />
                <img alt="thermometer body" id="therm-body-bg" style={styles.thermBodyBg} src={this.state.glassBodyImg} />
                <img
                  alt="thermometer mercury"
                  id="therm-body-mercury"
                  style={styles.thermBodyMercury}
                  src={this.state.redVerticalImg}
                />
                <div id="therm-body-fore" style={styles.thermBodyFore}></div>
                <img alt="thermometer bottom" id="therm-bottom" style={styles.thermBottom} src={this.state.glassBottomImg} />
                <div id="therm-tooltip" style={styles.thermTooltip}>
                  <img alt="thermometer left tip" className="tip-left" src={this.state.tootipPointImg} />
                  <div className="tip-middle" style={styles.thermToolTipMiddle}>
                    <p>{this.commaSeparateNumber(this.state.displayAmount)}</p>
                  </div>
                  <img alt="thermometer right tip" className="tip-right" src={this.state.tooltipButtImg} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Thermometer;
